/** The mean length of the moon's cycle, new moon to new moon, in days. */
const SYNODIC_MONTH = 29.530588853;
/** A known new moon: 6 January 2000, 18:14 UTC. */
const NEW_MOON = Date.UTC(2000, 0, 6, 18, 14);

/**
 * How far the moon is through its cycle at a moment: 0 new, 0.25 first quarter, 0.5 full, 0.75
 * last quarter. The mean cycle, so within a few hours of the almanac — plenty for a screen.
 */
export function moonPhase(at: Date): number {
  const days = (at.getTime() - NEW_MOON) / 86_400_000;
  return (((days / SYNODIC_MONTH) % 1) + 1) % 1;
}

/** Roughly how long the moon is up, for a rise or a set the forecast does not reach. */
const MOON_UP_MS = 12.4 * 3_600_000;

/**
 * Whether the moon is up at a moment, by the forecast's moonrises and moonsets, and if so how far
 * across its arc (0 rising, 1 setting). Undefined when the forecast does not reach the moment — a
 * preview of another day — so the screen keeps its own moon.
 */
export function moonUp(at: Date, rises: readonly Date[], sets: readonly Date[]): number | null | undefined {
  const all = [...rises, ...sets].map((d) => d.getTime());
  if (!all.length) return undefined;
  const t = at.getTime();
  const margin = 18 * 3_600_000;
  if (t < Math.min(...all) - margin || t > Math.max(...all) + margin) return undefined;

  const before = (list: readonly Date[]) => Math.max(-Infinity, ...list.map((d) => d.getTime()).filter((d) => d <= t));
  const after = (list: readonly Date[]) => Math.min(Infinity, ...list.map((d) => d.getTime()).filter((d) => d > t));
  const lastRise = before(rises);
  const lastSet = before(sets);
  const nextSet = after(sets);
  const nextRise = after(rises);
  // Up: it rose since it last set — or, early on, the next thing it does is set.
  const up = lastRise > lastSet || (lastRise === -Infinity && lastSet === -Infinity && nextSet < nextRise);
  if (!up) return null;
  const rise = lastRise > -Infinity ? lastRise : nextSet - MOON_UP_MS;
  const set = nextSet < Infinity ? nextSet : rise + MOON_UP_MS;
  return Math.min(1, Math.max(0, (t - rise) / (set - rise)));
}

/**
 * The lit part of the moon as seen from Denmark, as an SVG path on a disc of radius 50 around
 * (0, 0): growing from the right while waxing, shrinking to the left while waning. The edge on the
 * sun's side is a half circle; the terminator an ellipse that bulges out for a crescent and in for
 * a gibbous moon. Null when the moon is all but new.
 */
export function moonPath(phase: number): string | null {
  const lit = (1 - Math.cos(2 * Math.PI * phase)) / 2;
  if (lit < 0.02) return null;
  const waxing = phase < 0.5;
  const rx = Math.abs(Math.cos(2 * Math.PI * phase)) * 50;
  const limb = waxing ? 1 : 0;
  const terminator = (lit < 0.5) === waxing ? 0 : 1;
  return `M0 -50 A50 50 0 0 ${limb} 0 50 A${rx.toFixed(2)} 50 0 0 ${terminator} 0 -50 Z`;
}
